import { useQuery } from "@tanstack/react-query"
import { useAuthStore } from "@/stores/authStore"
import { api } from "@/lib/api"

interface ResourceUsage {
  used: number
  limit: number | null
  pct: number | null
}

interface UsageData {
  plan: {
    name: string
    plan_type: string
  }
  usage: {
    properties: ResourceUsage
    tenants: ResourceUsage
    leases: ResourceUsage
    documents: ResourceUsage
    storage_gb: ResourceUsage
  }
  features: Record<string, boolean>
  subscription: {
    billing_cycle: string
    status: string
    cancel_at_period_end: boolean
  } | null
}

const RESOURCE_MAP: Record<string, keyof UsageData["usage"]> = {
  properties: "properties",
  tenants: "tenants",
  leases: "leases",
  documents: "documents",
}

export function usePlan() {
  const { isAuthenticated } = useAuthStore()

  const { data } = useQuery<UsageData>({
    queryKey: ["plan", "usage"],
    queryFn: async () => {
      const { data } = await api.get("/api/plans/usage")
      return data.data
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
  })

  const planName = data?.plan?.name ?? "free"

  function checkLimit(resource: string, _currentCount?: number) {
    const key = RESOURCE_MAP[resource]
    if (!key || !data) {
      return { allowed: true, limit: Infinity, current: 0 }
    }
    const res = data.usage[key]
    const numLimit = res.limit === null ? Infinity : res.limit
    return {
      allowed: res.used < numLimit,
      limit: numLimit,
      current: res.used,
    }
  }

  return {
    plan: planName,
    data,
    checkLimit,
    isPaid: data?.plan?.plan_type !== "free",
    isPremium: data?.plan?.name === "premium",
  }
}
